import { AUTH_RELOAD_FAILURE, authReload } from 'actions/authActions'
import { logAmplitudeEvent, setAmplitudeIdentity } from 'amplitude/amplitude'
import { push } from 'connected-react-router'
import { incrementAppViewVersion } from 'ducks/app'
import { SET_AUTH, setOrgId } from 'ducks/auth'
import { viewModeActions } from 'ducks/viewMode'
import { SellerIntegrationType } from 'opensolar-common'
import { PartnerIdType } from 'pages/auth/sso/types'
import { GoogleLeadsConfigAndPlaceDataOrDefault } from 'pages/googleLeads/types'
import type { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { MfaErrorStatus } from 'ra-core'
import { showNotification } from 'react-admin'
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects'
import { createSelector } from 'reselect'
import { CustomFormUsageType } from 'resources/customForms/types'
import restClient from 'restClient'
import appStorage, { getOrgId } from 'storage/appStorage'
import { AccountingIntegration } from 'types/cashFlow/accounting'
import { ReduxActionType } from 'types/global'
import {
  ExpoModes,
  GetActionsResponseType,
  IntegrationEnabledType,
  MeasurementUnits,
  OrgFeatureSettings,
  OrgType,
  SupplierOrg,
} from 'types/orgs'
import { RootState } from 'types/state'
import { WorkflowType } from 'types/workflows'
import { createCachedReduxSelector } from 'util/createCachedReduxSelector'
import { currencySymbolForCountry } from 'util/misc'
import { fetchSellerIntegrations } from '../persistentContent/utils/InitSellerIntegrations'
import { authSelectors } from './auth'
import { setMfaCheckRequired, setMfaConfigRequired, setMfaHasDevices } from './auth_mfa_actions'
import { CLEAR_GOOGLE_LEADS_CONFIG } from './googleLeadsConfig'
import { updateSelectedHardwareSupplier } from './orderComponents'
import { doGetProjectViews, GET_PROJECT_VIEWS_SUCCESS, ProjectView } from './projectListViews'
import { changeTheme, OsTheme, setTheme } from './theme'
import { wsActions } from './websocket'

const API_URL = window.API_ROOT + '/api'

const selectSellerIntegrations = (state: RootState) => state.orgs.sellerIntegrations
const selectSupplierPreferences = (state: RootState) => state.orgs.supplierPreferences

const getEnabledHardwareDistributorFilterKeysAllSelector = createSelector([selectSellerIntegrations], (items) => {
  const keys: HardwareSupplierFilterKeyType[] = []
  items?.forEach((item: SellerIntegrationType) => {
    if (item.isEnabled) keys.push(item.filterKey)
  })
  return keys
})

const getEnabledHardwareDistributorFilterKeysRequireConnectedSelector = createSelector(
  [selectSellerIntegrations],
  (items) => {
    const keys: HardwareSupplierFilterKeyType[] = []
    items?.forEach((item: SellerIntegrationType) => {
      if (item.isEnabled && (item.isConnected ?? true)) keys.push(item.filterKey)
    })
    return keys
  }
)

const getEnabledSellerIntegrationsConfigAllSelector = createSelector([selectSellerIntegrations], (items) => {
  return items?.filter((item: SellerIntegrationType) => item.isEnabled) ?? []
})

const getEnabledSellerIntegrationsConfigRequireConnectedSelector = createSelector(
  [selectSellerIntegrations],
  (items) => {
    return items?.filter((item: SellerIntegrationType) => item.isEnabled && (item.isConnected ?? true)) ?? []
  }
)

const getSupplierPreferenceOrderSelector = createSelector([selectSupplierPreferences], (supplierPreferences) => {
  return (filterKey: string) => {
    const index = supplierPreferences?.indexOf(filterKey) ?? -1
    return index === -1 ? null : index
  }
})

export const GET_ORG = 'GET_ORG'
export const GET_ORG_LOADING = 'GET_ORG_LOADING'
export const GET_ORG_SUCCESS = 'GET_ORG_SUCCESS'
export const SET_ORG_SUCCESS = 'SET_ORG_SUCCESS'
export const GET_ORG_FAIL = 'GET_ORG_FAIL'
export const CLEAR_ORG = 'CLEAR_ORG'
export const UPDATE_ORG_WITH_CA_TEMPLATE = 'UPDATE_ORG_WITH_CA_TEMPLATE'
export const UPDATE_ORG_WITH_PANDADOC_CA_TEMPLATE = 'UPDATE_ORG_WITH_PANDADOC_CA_TEMPLATE'
export const JOIN_ORG_AND_SWITCH = 'JOIN_ORG_AND_SWITCH'
export const STORE_RATE_REFRESH_DATA = 'STORE_RATE_REFRESH_DATA'
export const STORE_FEATURE_SETTINGS = 'STORE_FEATURE_SETTINGS'
export const UPDATE_BLUESNAP_ONBOARIDNG_STATUS = 'UPATE_BLUESNAP_ONBOARDING_STATUS'
export const UPDATE_ACTIVE_ACCOUNTING_INTEGRATIONS = 'UPDATE_ACTIVE_ACCOUNTING_INTEGRATIONS'
export const ACCOUNTING_INTEGRATIONS = 'ACCOUNTING_INTEGRATIONS'
export const SET_ORG_FEATURE_SETTING = 'SET_ORG_FEATURE_SETTING'
export const INIT_SELLER_INTEGRATIONS = 'INIT_SELLER_INTEGRATIONS'
export const RELOAD_SELLER_INTEGRATIONS = 'RELOAD_SELLER_INTEGRATIONS'
export const SET_SUPPLIER_PREFERENCES = 'SET_SUPPLIER_PREFERENCES'
export const UPDATE_PREFERRED_STOCK_LOCATION = 'UPDATE_PREFERRED_STOCK_LOCATION'

export const updateActiveAccountingIntegrations = (activeIntegrations: string[]) => {
  return {
    type: UPDATE_ACTIVE_ACCOUNTING_INTEGRATIONS,
    payload: {
      activeIntegrations,
    },
  }
}

export const updateAccountingIntegrations = (accountingIntegrations: AccountingIntegration[]) => {
  return {
    type: ACCOUNTING_INTEGRATIONS,
    payload: {
      accountingIntegrations,
    },
  }
}

export type OrgReducerType = {
  loading: boolean
  org?: OrgType
  workflows?: WorkflowType[]
  sellerIntegrations?: SellerIntegrationType[]
  supplierPreferences?: string[]
  rate_refresh_reminder?: RateRefreshReminderType
}
export type RateRefreshReminderType = {
  cached_response: GetActionsResponseType | undefined
  last_fetched: string | undefined
  last_all_clear: string | undefined
}

const getInitialState = (): OrgReducerType => ({
  loading: false,
  org: appStorage.getOrg(),
})

export const orgsActions = {
  reloadOrg: (orgId?: number) => {
    return { type: GET_ORG, payload: { orgId } }
  },
  clearOrg: () => ({ type: CLEAR_ORG, payload: {} }),
  updateOrgWithCaTemplate: (orgId: number, newVal: boolean) => ({
    type: UPDATE_ORG_WITH_CA_TEMPLATE,
    payload: { orgId, newVal },
  }),
  updateOrgWithPandadocCaTemplate: (orgId: number, newVal: boolean) => ({
    type: UPDATE_ORG_WITH_PANDADOC_CA_TEMPLATE,
    payload: { orgId, newVal },
  }),
  joinOrgAndSwitch: (payload: { orgUrl: string; orgId: number; userId?: number }) => {
    return { type: JOIN_ORG_AND_SWITCH, payload }
  },
  storeRateRefreshData: (
    cached_response: GetActionsResponseType | undefined,
    last_fetched: string,
    last_all_clear: string | undefined
  ) => {
    return { type: STORE_RATE_REFRESH_DATA, payload: { cached_response, last_fetched, last_all_clear } }
  },
  setOrgFeatureSetting: (orgId: number, key: string, settings: OrgFeatureSettings) => {
    return { type: SET_ORG_FEATURE_SETTING, payload: { orgId, key, settings } }
  },
  initSellerIntegrations: (sellerIntegrations: SellerIntegrationType[]) => {
    return { type: INIT_SELLER_INTEGRATIONS, payload: { sellerIntegrations } }
  },
  reloadSellerIntegrations: (payload: { enableMultiDistributorShop: boolean; orgId?: number }) => {
    return { type: RELOAD_SELLER_INTEGRATIONS, payload }
  },
  setSupplierPreferences: (supplierPreferences: string[]) => {
    return { type: SET_SUPPLIER_PREFERENCES, payload: { supplierPreferences } }
  },
  updatePreferredStockLocation: (filterKey: string, preferredLocation: string) => {
    return { type: UPDATE_PREFERRED_STOCK_LOCATION, payload: { filterKey, preferredLocation } }
  },
}

export default function orgsReducer(state = getInitialState(), action: ReduxActionType): OrgReducerType {
  const newState = orgsInnerReducer(state, action)

  // clear org if logout
  // this is a quick hack to clear localStorage.org on logout until it is implemented properly
  if (action?.type === '@@router/LOCATION_CHANGE' && action?.payload?.location?.pathname === '/logout') {
    appStorage.setOrg(undefined)
    appStorage.setIso2(undefined)
    newState.org = undefined
  } else if (action?.payload?.hasOwnProperty('org')) {
    // Save any org changes to local storage
    appStorage.setOrg(newState.org)
    const iso2 = newState.org?.country?.iso2
    if (iso2) appStorage.setIso2(iso2)
  }
  return newState
}

// This is extracted from reducer simply to allow the local storage or org to work
function orgsInnerReducer(state: OrgReducerType, { type, payload }: ReduxActionType): OrgReducerType {
  switch (type) {
    case GET_ORG_LOADING:
      return {
        ...state,
        loading: true,
      }
    case GET_ORG_SUCCESS:
      const org: OrgType = payload.org

      var orgHasNMOS = !!org.external_account_id

      appStorage.setString('api_key_google', org.api_key_google)

      window.hasNearmapOnOpenSolarInjectedFromReact = function () {
        return orgHasNMOS
      }

      //Inject Nearmap and Google API Keys so they can be used by the window.* outside React
      // If api_key not set then we will clear any exising value
      if (window.AccountHelper) {
        // Note that org may be null for customers so ensure we do not throw an exception
        window.AccountHelper.overrideApiKeyVexcel = org.api_key_vexcel
        window.AccountHelper.overrideApiKeyNearmap = org.api_key_nearmap
        window.AccountHelper.overrideApiKeyArcGisOSM = org.api_key_arcgis_osm
        window.AccountHelper.overrideApiKeyGoogle3DTiles = org.api_key_google_3d_tiles

        const auth = appStorage.getAuth()
        // Use org override if available, otherwise use generic value at top level
        var apiKeyGoogle = org.api_key_google || auth?.api_key_google

        // Never clear overrideApiKeyGoogle, only overwrite with a new (not-empty) value
        // to avoid some race-condition bugs
        if (apiKeyGoogle) {
          window.AccountHelper.overrideApiKeyGoogle = apiKeyGoogle
        }
      }

      return {
        ...state,
        loading: false,
        org: payload.org,
        workflows: payload.workflows,
        supplierPreferences: payload.supplierPreferences,
        rate_refresh_reminder: undefined,
      }
    case GET_ORG_FAIL:
      return {
        ...state,
        loading: false,
        org: undefined,
        rate_refresh_reminder: undefined,
      }
    case CLEAR_ORG:
      appStorage.setString('api_key_google')
      return {
        ...state,
        loading: false,
        org: undefined,
        rate_refresh_reminder: undefined,
      }
    case UPDATE_ACTIVE_ACCOUNTING_INTEGRATIONS:
      if (!state.org) {
        return { ...state }
      } else {
        return {
          ...state,
          org: {
            ...state.org,
            active_accounting_integrations: payload.activeIntegrations,
          },
        }
      }

    case ACCOUNTING_INTEGRATIONS:
      if (!state.org) {
        return { ...state }
      } else {
        const updatedState = {
          ...state,
          org: {
            ...state.org,
            accounting_integrations: payload.accountingIntegrations,
          },
        }
        return updatedState
      }

    case UPDATE_ORG_WITH_CA_TEMPLATE:
      if (!state.org?.docusign_config) {
        throw new Error("Can't update Org's docusign config as it doesn't exist")
      }
      if (state.org?.id !== payload.orgId) {
        throw new Error("Can't update Org's docusign config, not current Org")
      }
      return {
        ...state,
        org: {
          ...state.org,
          docusign_config: {
            ...state.org.docusign_config,
            enable_california_protection_guide: payload.newVal,
          },
        },
      }
    case UPDATE_ORG_WITH_PANDADOC_CA_TEMPLATE:
      if (!state.org?.pandadoc_config) {
        throw new Error("Can't update Org's pandadoc config as it doesn't exist")
      }
      if (state.org?.id !== payload.orgId) {
        throw new Error("Can't update Org's pandadoc config, not current Org")
      }
      return {
        ...state,
        org: {
          ...state.org,
          pandadoc_config: {
            ...state.org.pandadoc_config,
            enable_california_protection_guide: payload.newVal,
          },
        },
      }
    case STORE_RATE_REFRESH_DATA:
      return {
        ...state,
        rate_refresh_reminder: {
          cached_response: payload?.cached_response,
          last_fetched: payload?.last_fetched,
          last_all_clear: payload?.last_all_clear,
        },
      }
    case STORE_FEATURE_SETTINGS:
      if (!state.org) {
        console.warn('Failed to store feature settings, no org found')
        return state
      }
      return {
        ...state,
        org: {
          ...state.org,
          feature_settings: payload as Record<string, OrgFeatureSettings>,
        },
      }
    case INIT_SELLER_INTEGRATIONS:
      return {
        ...state,
        sellerIntegrations: payload.sellerIntegrations,
      }
    case SET_SUPPLIER_PREFERENCES:
      return {
        ...state,
        supplierPreferences: payload.supplierPreferences,
      }
    case UPDATE_PREFERRED_STOCK_LOCATION:
      return {
        ...state,
        sellerIntegrations: state.sellerIntegrations?.map((sellerIntegration) => {
          if (sellerIntegration.filterKey === payload.filterKey) {
            return { ...sellerIntegration, preferredStockLocation: payload.preferredLocation }
          }
          return sellerIntegration
        }),
      }
    default:
      return { ...state }
  }
}

export function doLoadOrg(orgId: number, put, push): Promise<OrgType> {
  return new Promise<OrgType>((resolve, reject) => {
    const restClientInstance = restClient(API_URL)
    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/as_object/`,
    })
      .then((response: any) => {
        const org: OrgType = response.data
        resolve(org)
      })
      .catch((err: any) => {
        console.warn('Failed to load Org data: ', err)
        reject(err)
      })
  })
}

export function doConnectSupplierReferral(
  orgId: number,
  urlFragment: string
): Promise<{ connected: boolean; supplier_key?: string }> {
  return new Promise((resolve, reject) => {
    const restClientInstance = restClient(API_URL)
    restClientInstance('CUSTOM_POST', 'custom', {
      url: 'supplier_referral_connect/',
      data: { url_fragment: urlFragment, org_id: orgId },
    })
      .then((response: any) => resolve(response?.data || {}))
      .catch((err: any) => reject(err))
  })
}

export function doLoadSupplierPreferences(orgId: number): Promise<string[]> {
  return new Promise<string[]>((resolve, reject) => {
    const restClientInstance = restClient(API_URL)
    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/hardware_supplier_preferences/`,
    })
      .then((response: any) => {
        const preferences: string[] = response.data
        resolve(preferences)
      })
      .catch((err: any) => {
        if (!err.body?.mfa_status) console.warn('Failed to load Org data: ', err)
        // MFA errors will be handled in `useLogoutIfAccessDenied.ts`
        reject(err)
      })
  })
}

export function loadOrgWorkflows(orgId: number): Promise<WorkflowType[]> {
  return new Promise<WorkflowType[]>((resolve, reject) => {
    const restClientInstance = restClient(API_URL)
    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/workflows/?show_archived=1&owned_by=${orgId}`,
    })
      .then((response: any) => {
        const workflows: WorkflowType[] = response.data
        resolve(workflows)
      })
      .catch((err: any) => {
        if (!err.body?.mfa_status) console.warn('Failed to load Org data: ', err)
        // MFA errors will be handled in `useLogoutIfAccessDenied.ts`
        reject(err)
      })
  })
}

export function* getCurrentOrgSaga({ payload }: ReduxActionType) {
  try {
    let orgId = payload.orgId
    if (orgId === undefined) {
      orgId = yield select(authSelectors.getOrgId)
      if (orgId === undefined) orgId = getOrgId()
    }

    const currentOrg = yield select(orgSelectors.getOrg)
    const isOrgSwitch = currentOrg?.id !== orgId

    yield put({ type: GET_ORG_LOADING, payload })
    const res: [
      OrgType | undefined,
      WorkflowType[] | undefined,
      ProjectView[] | undefined,
      string[] | undefined,
      GoogleLeadsConfigAndPlaceDataOrDefault,
    ] = yield all([
      call(doLoadOrg, orgId, put, push),
      call(loadOrgWorkflows, orgId),
      call(doGetProjectViews, orgId),
      call(doLoadSupplierPreferences, orgId),
    ])
    if (!res) yield put({ type: GET_ORG_FAIL })
    else {
      yield put({ type: GET_ORG_SUCCESS, payload: { org: res[0], workflows: res[1], supplierPreferences: res[3] } })
      yield put({ type: GET_PROJECT_VIEWS_SUCCESS, payload: { project_list_views_data: res[2] } })
      var theme: OsTheme = res[0]?.external_account_id ? 'nearmap' : 'opensolar'
      yield put(changeTheme(theme))
      yield put(wsActions.checkConnection())
      yield put({ type: SET_ORG_SUCCESS })
      if (isOrgSwitch) {
        yield put({
          type: CLEAR_GOOGLE_LEADS_CONFIG,
        })
      }
    }
  } catch (e) {
    yield put(wsActions.checkConnection())
    yield put(setTheme('opensolar'))
    yield put({ type: GET_ORG_FAIL, error: e })

    const body = (e as any).body

    const mfaStatus: MfaErrorStatus | undefined = body?.mfa_status
    if (mfaStatus) {
      console.debug('MFA issue, redirecting to MFA setup', body?.mfa_status)
      yield put(setMfaHasDevices(!!body?.has_sms_device, !!body?.has_totp_device, !!body?.has_recovery_codes))
      yield put(setMfaCheckRequired(mfaStatus === 'mfa_required' || mfaStatus === 'mfa_rejected'))
      yield put(setMfaConfigRequired(mfaStatus === 'mfa_required_but_not_configured'))
      yield put(push('/login'))
      return true
    }
  }
}

export function* setOrgSuccessEffect() {
  const org = yield select(orgSelectors.getOrg)
  const user = yield select(authSelectors.getCurrentUser)
  const role = yield select(authSelectors.getCurrentRole)
  const isStaff = yield select(authSelectors.getIsStaff)
  const isAnonymous = yield select(authSelectors.getIsAnonymous)

  // set header state
  const isLinkedIronRidgeOrg = yield select((state) => orgSelectors.getIsPartnerOrgLinked(state, 'ironridge'))
  yield put(viewModeActions.setBrandingState(isLinkedIronRidgeOrg ? 'ironridge' : 'opensolar'))

  setAmplitudeIdentity({
    orgId: org.id,
    orgName: org.name,
    userId: user.id,
    roleId: role.id,
    isStaff,
    isAnonymous,
    userEmail: role.email || user.email,
    phone: role.phone || user.phone,
    countryIso2: org.country?.iso2 ?? org.country_iso2,
  })

  logAmplitudeEvent('login_success')
}

export const accountingIntegrations = async (dispatch, orgId, roleId) => {
  try {
    const API_URL = window.API_ROOT + '/api'
    const restClientInstance = restClient(API_URL)
    const response = await restClientInstance('CUSTOM_GET', 'custom', {
      url: 'orgs/' + orgId + '/accounting/connections/',
    })
    const accResponse = response.data

    if (!accResponse || !Array.isArray(accResponse)) {
      console.error('Unexpected API response structure:', response)
      return
    }

    const mappedData: AccountingIntegration[] = accResponse.map((acc) => {
      return {
        name: acc.name || '',
        integration_id: acc.integration_id || '',
        status: acc.status || 'disconnected',
        connection_url: acc.connection_url || null,
        created_at: acc.created_at || null,
        provider: acc.provider || null,
        product_ledger_account_id: acc.product_ledger_account_id || null,
        payment_ledger_account_id: acc.payment_ledger_account_id || null,
        accounting_contact_email: acc.accounting_contact_email || null,
        provider_account_name: acc.provider_account_name || null,
        tax_rate_json: acc.tax_rate_json || null,
      }
    })
    dispatch(updateAccountingIntegrations(mappedData))

    return mappedData
  } catch (error) {
    console.error('Error fetching accounting integrations: ', error)
  }
}

export const createRole = ({ orgUrl, userId }) => {
  return new Promise((resolve, reject) => {
    const restClientInstance = restClient(API_URL)
    restClientInstance('CUSTOM_POST', 'custom', {
      url: 'global_roles/',
      data: {
        is_admin: true,
        org: orgUrl,
        permissions_role: 1,
        is_hidden: true,
        user: `${window.API_ROOT_ABSOLUTE}/auth/users/${userId}/`,
      },
    })
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        console.warn('Error trying to create role: ', error)
        reject(error)
      })
  })
}

export function* joinOrgAndSwitch({ payload }: ReduxActionType) {
  yield call(createRole, payload)
  yield put(authReload({ reloadOrg: false })) // load latest auth data by existing orgId
  yield take([SET_AUTH, AUTH_RELOAD_FAILURE]) // wait for auth reload to complete, otherwise auth check will fail on some pages
  yield put(setOrgId({ orgId: payload.orgId })) // reloads org data by new orgId
  yield put(incrementAppViewVersion())
  yield put(showNotification('Switch Success', 'info'))
}

export const orgSelectors = {
  getOrgLoading: (state: RootState) => {
    return state.orgs.loading
  },
  getOrg: (state: RootState) => {
    return state.orgs.org
  },
  getOrgUrl: (state: RootState) => {
    return state.orgs.org?.url
  },
  getConnectedOrgs: createCachedReduxSelector((state: RootState) => {
    return state.orgs.org?.connected_orgs || []
  }),
  getWorkflows: createCachedReduxSelector((state: RootState) => {
    return state.orgs.workflows || []
  }),
  getOrgSellerIntegrations: (state: RootState) => {
    return state.orgs.sellerIntegrations
  },
  getOrgSupplierPreferences: (state: RootState) => {
    return state.orgs.supplierPreferences
  },
  getOrgCountryId: (state: RootState) => {
    return orgSelectors.getOrg(state)?.country?.id
  },
  getOrgIso2: (state: RootState) => {
    return orgSelectors.getOrg(state)?.country?.iso2
  },
  getIsPartnerOrgLinked: createCachedReduxSelector((state: RootState, partner: PartnerIdType) => {
    const current_org = orgSelectors.getOrg(state)
    return !!current_org?.linked_partner_orgs?.[partner]
  }),
  getExpoEnabled: (state: RootState) => {
    const current_org = orgSelectors.getOrg(state)
    return current_org?.expo_mode === ExpoModes.Basic || current_org?.expo_mode === ExpoModes.Premium
  },
  getPremiumExpo: (state: RootState) => {
    const current_org = orgSelectors.getOrg(state)
    return current_org?.expo_mode === ExpoModes.Premium
  },
  getEnableOutlet: (state: RootState) => {
    const current_org = orgSelectors.getOrg(state)
    return orgSelectors.getOrgIso2(state) === 'AU' && !!current_org?.enable_outlet_integration
  },
  getEnableUKHardwareSuppliers: createCachedReduxSelector((state: RootState) => {
    const ukSuppliers: HardwareSupplierFilterKeyType[] = ['city_plumbing']
    return ukSuppliers.filter((v: HardwareSupplierFilterKeyType) =>
      orgSelectors.getEnabledHardwareDistributorFilterKeys(state).includes(v)
    )
  }),
  getEnabledHardwareSuppliers: createCachedReduxSelector((state: RootState): HardwareSupplierFilterKeyType[] => {
    return orgSelectors.getEnabledHardwareDistributorFilterKeys(state)
  }),
  getEnabledHardwareDistributorFilterKeys: createCachedReduxSelector(
    (state: RootState): HardwareSupplierFilterKeyType[] => {
      const sellerIntegrations = orgSelectors.getOrgSellerIntegrations(state)
      if (!sellerIntegrations) return []
      const enabledHardwareDistributorFilterKeys: HardwareSupplierFilterKeyType[] = []
      sellerIntegrations.forEach((item: SellerIntegrationType) => {
        if (item.isEnabled) {
          enabledHardwareDistributorFilterKeys.push(item.filterKey)
        }
      })
      return enabledHardwareDistributorFilterKeys
    }
  ),
  getEnabledSellerIntegrationsConfig: createCachedReduxSelector((state: RootState): SellerIntegrationType[] => {
    const sellerIntegrations = orgSelectors.getOrgSellerIntegrations(state)
    return sellerIntegrations?.filter((item: SellerIntegrationType) => item.isEnabled) ?? []
  }),
  getSupplierPreferenceOrder: (state: RootState, filterKey: string): number | null => {
    const supplierPreferences = orgSelectors.getOrgSupplierPreferences(state)
    const index = supplierPreferences?.indexOf(filterKey) ?? -1
    return index === -1 ? null : index
  },
  getSelectedHardwareSupplier: (state: RootState): HardwareSupplierFilterKeyType | undefined => {
    let selectedSupplier = state.orderComponents.selectedHardwareSupplier
    let enabledSuppliers: HardwareSupplierFilterKeyType[] = orgSelectors.getEnabledHardwareDistributorFilterKeys(state)

    if (selectedSupplier && enabledSuppliers.includes(selectedSupplier)) {
      return selectedSupplier
    } else {
      appStorage.clear('selected_hardware_supplier')
    }

    if (enabledSuppliers.length > 0) {
      let defaultSupplier = enabledSuppliers[0]
      window.reduxStore.dispatch(updateSelectedHardwareSupplier(defaultSupplier))
      return defaultSupplier
    }
    return undefined
  },
  getSelectedHardwareSupplierFilterKey: (state: RootState): HardwareSupplierFilterKeyType | undefined => {
    return orgSelectors.getSelectedHardwareSupplier(state)
  },
  getRegistrableHardwareSuppliersByCountry: createCachedReduxSelector(
    (state: RootState): HardwareSupplierFilterKeyType[] => {
      const countryIso2 = orgSelectors.getOrgIso2(state)
      if (countryIso2 === 'AU') {
        return ['outlet']
      } else if (countryIso2 === 'GB') {
        return []
      } else {
        return []
      }
    }
  ),
  distributorRegistrationRequired: (state: RootState): HardwareSupplierFilterKeyType | undefined => {
    const selectedDistributor = orgSelectors.getSelectedHardwareSupplier(state)
    const availableDistributorsToRegister = orgSelectors.getRegistrableHardwareSuppliersByCountry(state)
    const enabledDistributors = orgSelectors.getEnabledHardwareSuppliers(state)
    if (!selectedDistributor) {
      return
    }

    if (
      !enabledDistributors.includes(selectedDistributor) &&
      availableDistributorsToRegister.includes(selectedDistributor)
    ) {
      return selectedDistributor
    } else {
      return undefined
    }
  },
  getEnableAutoDesign: (state: RootState) => {
    const current_org = orgSelectors.getOrg(state)
    return !!current_org?.enable_auto_design
  },
  getIsNearmapOrg: (state: RootState) => {
    const current_org = orgSelectors.getOrg(state)
    return !!current_org?.external_account_id
  },
  getEnableExhibit: (state: RootState) => {
    const current_org = orgSelectors.getOrg(state)
    return !!current_org?.partner_org?.is_active
  },
  getProFeaturesEnabled: (state: RootState) => {
    const current_org = orgSelectors.getOrg(state)
    return !current_org?.partner_org?.is_active || !!current_org?.partner_org?.is_pro_features_enabled
  },
  getEnableShowcase: (state: RootState) => {
    const current_org = orgSelectors.getOrg(state)
    return !!current_org?.showcase_org?.is_showcase_enabled
  },
  getCobrandingOrg: (state: RootState) => {
    const current_org = orgSelectors.getOrg(state)
    return !!current_org?.cobranding_org
  },
  getCustomForms: createCachedReduxSelector((state: RootState): CustomFormUsageType[] => {
    const current_org = orgSelectors.getOrg(state)
    return current_org?.custom_forms || []
  }),
  getSupplierFeature: (state: RootState, key: keyof SupplierOrg) => {
    const current_org = orgSelectors.getOrg(state)
    return current_org?.supplier_org?.[key] ?? false
  },
  hasAllOrgFeatures: (state: RootState, features: string[]) => {
    const current_org = orgSelectors.getOrg(state)
    if (!current_org) return false
    for (const feature of features) {
      if (!current_org['enable_' + feature]) return false
    }
    return true
  },
  getAvailableFinanceIntegrations: createCachedReduxSelector((state: RootState) => {
    const current_org = orgSelectors.getOrg(state)
    return current_org?.available_integrations?.finance || []
  }),
  getCentrixCreditCheck: (state: RootState) => {
    const current_org = orgSelectors.getOrg(state)
    return current_org?.enable_centrix_credit_check
  },
  getSungageIntegrationStatus: (state: RootState) => {
    return orgSelectors.getOrg(state)?.enable_sungage
  },
  getSunlightIntegrationStatus: (state: RootState) => {
    return orgSelectors.getOrg(state)?.enable_sunlight
  },
  getMosaicIntegrationStatus: (state: RootState) => {
    return orgSelectors.getOrg(state)?.enable_mosaic
  },
  getLoanpalIntegrationStatus: (state: RootState) => {
    return orgSelectors.getOrg(state)?.enable_loanpal
  },
  getDividendIntegrationStatus: (state: RootState) => {
    return orgSelectors.getOrg(state)?.enable_dividend
  },
  getPhoenixIntegrationStatus: (state: RootState) => {
    return orgSelectors.getOrg(state)?.enable_phoenix
  },
  getBrighteIntegrationStatus: (state: RootState) => {
    return orgSelectors.getOrg(state)?.enable_brighte
  },
  getSelinaIntegrationStatus: (state: RootState) => {
    return orgSelectors.getOrg(state)?.enable_selina
  },
  getCheckoutFinanceStatus: (state: RootState) => {
    const org = orgSelectors.getOrg(state)
    return org?.enable_28watt || org?.enable_figure_lending || org?.enable_loan_options_ai
  },
  getPropensioStatus: (state: RootState) => {
    return orgSelectors.getOrg(state)?.enable_propensio
  },
  getGenericIntegrationStatus: createCachedReduxSelector(
    (state: RootState, integration_name: string): IntegrationEnabledType => {
      // @ts-ignore these keys are dynamic and driven by the db, not sure how to type this properly
      return orgSelectors.getOrg(state)?.[`enable_${integration_name}`]
    }
  ),
  getCachedRefreshReminder: (state: RootState) => {
    return state.orgs.rate_refresh_reminder
  },
  getCurrencySymbolFromOrg: (state: RootState) => {
    const currentOrgIso2 = orgSelectors.getOrgIso2(state)
    if (currentOrgIso2) return currencySymbolForCountry(currentOrgIso2)
    else return '$'
  },
  getCashFlowIsActive: (state: RootState) => {
    return orgSelectors.getOrg(state)?.cashflow_is_active
  },
  getCashFlowOnboardingStatus: (state: RootState) => {
    return orgSelectors.getOrg(state)?.cashflow_onboarding_status
  },
  getCashflowPspOnboardings: (state: RootState) => {
    return orgSelectors.getOrg(state)?.cashflow_psp_onboardings || []
  },
  getCashflowPreferredPspIntegration: (state: RootState) => {
    return orgSelectors.getOrg(state)?.preferred_cashflow_psp_integration
  },
  getBlueSnapCashflowOnboarding: (state: RootState) => {
    return orgSelectors.getCashflowPspOnboardings(state).find((entry) => entry.psp_integration === 'bluesnap')
  },
  getStripeCashflowOnboarding: (state: RootState) => {
    return orgSelectors.getCashflowPspOnboardings(state).find((entry) => entry.psp_integration === 'stripe')
  },
  getBlueSnapOnboardingIsSubmitted: (state: RootState) => {
    const statusValue = orgSelectors.getBlueSnapCashflowOnboarding(state)?.status || orgSelectors.getCashFlowOnboardingStatus(state)
    return statusValue && statusValue !== 'created'
  },
  getBlueSnapOnboardingIsStarted: (state: RootState) => {
    const statusValue = orgSelectors.getBlueSnapCashflowOnboarding(state)?.status || orgSelectors.getCashFlowOnboardingStatus(state)
    return Boolean(statusValue)
  },
  getBlueSnapOnboardingIsInProgress: (state: RootState) => {
    const isStarted = orgSelectors.getBlueSnapOnboardingIsStarted(state)
    const isSubmitted = orgSelectors.getBlueSnapOnboardingIsSubmitted(state)
    return isStarted && !isSubmitted
  },
  getBlueSnapOnboardingIsApproved: (state: RootState) => {
    const statusValue = orgSelectors.getBlueSnapCashflowOnboarding(state)?.status || orgSelectors.getCashFlowOnboardingStatus(state)
    return statusValue === 'approved' || statusValue === 'conditionally approved'
  },
  getStripeOnboardingIsApproved: (state: RootState) => {
    const statusValue = orgSelectors.getStripeCashflowOnboarding(state)?.status
    return statusValue === 'approved' || statusValue === 'conditionally approved'
  },
  getIsCashflowStripePsp: (state: RootState) => {
    return orgSelectors.getCashFlowPspIntegration(state) === 'stripe'
  },
  getIsCashflowBlueSnapPsp: (state: RootState) => {
    return orgSelectors.getCashFlowPspIntegration(state) === 'bluesnap'
  },
  getCashFlowPspIntegration: (state: RootState) => {
    return orgSelectors.getOrg(state)?.cashflow_psp_integration
  },
  getMeasurementUnits: (state: RootState): MeasurementUnits | undefined => {
    const org = orgSelectors.getOrg(state)
    return org?.measurement_units
  },
  getActiveAccountingIntegrations: (state: RootState) => {
    const org = orgSelectors.getOrg(state)
    return org?.active_accounting_integrations
  },
  getAccountingIntegrations: createCachedReduxSelector((state: RootState) => {
    const org = orgSelectors.getOrg(state)
    return org?.accounting_integrations || []
  }),
  getOrgFeatureSettings: createCachedReduxSelector((state: RootState) => {
    const org = orgSelectors.getOrg(state)
    return org?.feature_settings || {}
  }),
}

export function* orgsSagas() {
  yield all([
    takeEvery(GET_ORG, getCurrentOrgSaga),
    takeEvery(JOIN_ORG_AND_SWITCH, joinOrgAndSwitch),
    takeEvery(SET_ORG_SUCCESS, setOrgSuccessEffect),
    takeEvery(SET_ORG_FEATURE_SETTING, setOrgFeatureSetting),
    takeEvery(RELOAD_SELLER_INTEGRATIONS, reloadSellerIntegrationsSaga),
  ])
}

// When the Pro connects with the supplier, we have to reload Supplier Integration to retrieve the most recent "connection" status.
// With this, the UI like "Connect" button can transition to "Shop now" as we re-render from this redux change.
// Note: The business Logic here is identical from `InitSellerIntegrations.tsx`
function* reloadSellerIntegrationsSaga({ payload }: ReduxActionType) {
  const org = yield select(orgSelectors.getOrg)
  const isAuthenticatedPro = yield select(authSelectors.getIsAuthenticatedPro)
  const resolvedOrgId = payload.orgId ?? org?.id
  const enableMultiDistributorShop = payload.enableMultiDistributorShop

  if (!resolvedOrgId || !isAuthenticatedPro) return
  const sellerURL = enableMultiDistributorShop
    ? `orgs/${resolvedOrgId}/available_hardware_distributors/`
    : `orgs/${resolvedOrgId}/seller_integrations/?is_enabled=true`

  try {
    const sellerIntegrations = yield call(fetchSellerIntegrations, sellerURL, API_URL)
    yield put(orgsActions.initSellerIntegrations(sellerIntegrations))
  } catch (error) {
    console.warn('Failed to reload seller integrations:', error)
  }
}

function* setOrgFeatureSetting(action: ReduxActionType) {
  const { payload } = action
  const { orgId, key, settings } = payload
  const org = yield select(orgSelectors.getOrg)
  if (!org) return
  const newFeatureSettings = { ...org.feature_settings, [key]: settings }
  yield call(saveOrgConfig, { orgId, key: 'feature_settings', config: newFeatureSettings })
  yield put({ type: STORE_FEATURE_SETTINGS, payload: newFeatureSettings })
}

export const saveOrgConfig = ({ orgId, key, config }: { orgId: number; key: string; config: any }) => {
  return new Promise((resolve, reject) => {
    const restClientInstance = restClient(API_URL)
    restClientInstance('CUSTOM_PUT', 'custom', {
      url: `orgs/${orgId}/configs/`,
      data: {
        [key]: config,
      },
    })
      .then(() => {
        console.debug('org config updated')
        resolve(config)
      })
      .catch((err) => {
        console.warn('Failed to update org config', err)
        reject(err)
      })
  })
}
